import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, Save, User, Mail, Phone, Shield, Check, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface UsuarioEventoForm {
  nome: string;
  telefone: string;
  email: string;
  tipoUsuario: 'Agência' | 'Promotor';
  solucoes: string[];
}

export default function EventsUsuarioForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const url = searchParams.get('url');
  const [formData, setFormData] = useState<UsuarioEventoForm>({
    nome: '',
    telefone: '',
    email: '',
    tipoUsuario: 'Promotor',
    solucoes: []
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showSolucoesDropdown, setShowSolucoesDropdown] = useState(false);

  // Mock data para soluções disponíveis
  const solucoesDisponiveis = [
    "Quiz Interativo",
    "Caça ao Tesouro", 
    "Enquete ao Vivo",
    "Roda da Sorte"
  ];

  const isEditing = !!id;

  useEffect(() => {
    if (isEditing) {
      // Simular carregamento de dados do usuário
      const mockUsuario = {
        nome: "Carlos Silva",
        telefone: "(11) 99999-1234",
        email: "carlos@agencia.com",
        tipoUsuario: "Agência" as const,
        solucoes: ["Quiz Interativo", "Caça ao Tesouro"]
      };
      setFormData(mockUsuario);
    }
  }, [isEditing]);

  const handleInputChange = (field: keyof UsuarioEventoForm, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSolucaoToggle = (solucao: string) => {
    setFormData(prev => ({
      ...prev,
      solucoes: prev.solucoes.includes(solucao)
        ? prev.solucoes.filter(s => s !== solucao)
        : [...prev.solucoes, solucao]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Simular envio para API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Sucesso",
        description: isEditing 
          ? "Usuário atualizado com sucesso!" 
          : "Usuário adicionado com sucesso!",
      });

      navigate(`/eventos-admin/gestao-acesso${location.search}`);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível salvar o usuário",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/eventos-admin/gestao-acesso${location.search}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleBack}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <ArrowLeft className="text-foreground" size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-[16px] font-bold text-foreground">
            {isEditing ? "Editar Usuário" : "Novo Usuário"}
          </h1>
          <p className="text-[13px] text-muted-foreground">
            {isEditing 
              ? "Edite as informações do usuário do evento" 
              : "Adicione um novo usuário ao evento"
            }
          </p>
          {url && (
            <p className="text-sm text-brand-logo-1 mt-1">
              Conectado a: {decodeURIComponent(url)}
            </p>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="bg-card rounded-xl border border-border p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Nome */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Nome Completo *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
              <input
                type="text"
                required
                value={formData.nome}
                onChange={(e) => handleInputChange('nome', e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-muted border border-border rounded-lg text-foreground placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-logo-1"
                placeholder="Digite o nome completo"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              E-mail *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-muted border border-border rounded-lg text-foreground placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-logo-1"
                placeholder="Digite o e-mail"
              />
            </div>
          </div>

          {/* Telefone */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Telefone *
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
              <input
                type="tel"
                required
                value={formData.telefone}
                onChange={(e) => handleInputChange('telefone', e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-muted border border-border rounded-lg text-foreground placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-logo-1"
                placeholder="(11) 99999-9999"
              />
            </div>
          </div>

          {/* Tipo de Usuário */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Tipo de Usuário *
            </label>
            <div className="grid grid-cols-2 gap-3">
              {(['Agência', 'Promotor'] as const).map((tipo) => (
                <button
                  key={tipo}
                  type="button"
                  onClick={() => handleInputChange('tipoUsuario', tipo)}
                  className={`p-3 rounded-lg border transition-all duration-200 flex items-center gap-3 ${
                    formData.tipoUsuario === tipo
                      ? 'border-brand-logo-1 bg-brand-logo-1/10 text-brand-logo-1'
                      : 'border-border bg-muted text-foreground hover:border-brand-logo-1/50'
                  }`}
                >
                  <Shield size={20} />
                  <span className="font-medium">{tipo}</span>
                  {formData.tipoUsuario === tipo && (
                    <Check size={20} className="ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Soluções */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Soluções *
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowSolucoesDropdown(!showSolucoesDropdown)}
                className="w-full p-3 bg-muted border border-border rounded-lg text-left text-foreground hover:border-brand-logo-1/50 transition-colors flex items-center justify-between"
              >
                <span className={formData.solucoes.length > 0 ? 'text-foreground' : 'text-muted-foreground'}>
                  {formData.solucoes.length > 0 
                    ? `${formData.solucoes.length} solução(ões) selecionada(s)`
                    : 'Selecione as soluções'
                  }
                </span>
                <Shield size={20} className="text-muted-foreground" />
              </button>

              {showSolucoesDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-muted border border-border rounded-ds-card shadow-lg z-10 max-h-60 overflow-y-auto">
                  {solucoesDisponiveis.map((solucao) => (
                    <button
                      key={solucao}
                      type="button"
                      onClick={() => handleSolucaoToggle(solucao)}
                      className="w-full p-3 text-left text-foreground hover:bg-brand-logo-1/10 transition-colors flex items-center gap-3"
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        formData.solucoes.includes(solucao)
                          ? 'border-brand-logo-1 bg-brand-logo-1'
                          : 'border-border'
                      }`}>
                        {formData.solucoes.includes(solucao) && (
                          <Check size={12} className="text-foreground" />
                        )}
                      </div>
                      <span>{solucao}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Soluções selecionadas */}
            {formData.solucoes.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {formData.solucoes.map((solucao) => (
                  <div
                    key={solucao}
                    className="flex items-center gap-2 px-3 py-1 bg-brand-logo-1/10 text-brand-logo-1 rounded-full text-sm"
                  >
                    <span>{solucao}</span>
                    <button
                      type="button"
                      onClick={() => handleSolucaoToggle(solucao)}
                      className="hover:bg-brand-logo-1/20 rounded-full p-1"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-6">
            <button
              type="button"
              onClick={handleBack}
              className="px-6 py-3 text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading || formData.solucoes.length === 0}
              className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={20} />
              {isLoading ? "Salvando..." : (isEditing ? "Atualizar" : "Adicionar")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}